/**
 * W66.G — Organizational Mapping
 * Route: /account/:accountId/org-map
 *
 * Advisory only — does not affect M-PACT composite score.
 * Displays a vertical tree of people inside the target organization:
 * buying committee, power dynamics, sentiment.
 *
 * Canon: No exclamation points. No emojis. M-PACT hyphenated.
 * Banned: "People Mapping", "Fit" as CP, "Compelling event", "Amber".
 */

import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "wouter";
import { Plus, ArrowLeft, Users } from "lucide-react";
import { api, getSession } from "@/lib/session";
import type { OrgNode } from "@/components/org-map/types";
import OrgNodeCard from "@/components/org-map/NodeCard";
import AddNodeModal from "@/components/org-map/AddNodeModal";
import DocumentTitle from "@/components/DocumentTitle";

// ─── Types ─────────────────────────────────────────────────────────────────────

interface AccountMeta {
  id: number;
  name: string;
  industry: string | null;
  owner_user_id: string;
  ownerId?: string;
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function AccountOrgMap() {
  const { accountId } = useParams<{ accountId: string }>();
  const acctId = Number(accountId);

  const [account, setAccount] = useState<AccountMeta | null>(null);
  const [nodes, setNodes] = useState<OrgNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [notice, setNotice] = useState<{ kind: "success" | "error"; text: string } | null>(null);

  const session = getSession();
  const userRole = session?.role ?? "";
  const userId = session?.id ?? "";

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  function canEdit(): boolean {
    if (!account || !session) return false;
    if (userRole === "manager" || userRole === "exec" || userRole === "executive") return true;
    return (account.owner_user_id ?? account.ownerId) === userId;
  }

  const fetchNodes = useCallback(async () => {
    if (!Number.isFinite(acctId) || acctId <= 0) return;
    try {
      const data = await api<OrgNode[]>(`/api/account-org-map/${acctId}/nodes`);
      setNodes(data ?? []);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load org map");
    }
  }, [acctId]);

  const fetchAccount = useCallback(async () => {
    if (!Number.isFinite(acctId) || acctId <= 0) return;
    try {
      const data = await api<AccountMeta>(`/api/accounts/${acctId}`);
      setAccount(data);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load account");
    }
  }, [acctId]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([fetchAccount(), fetchNodes()]).finally(() => setLoading(false));
  }, [fetchAccount, fetchNodes]);

  // ─── Actions ─────────────────────────────────────────────────────────────────

  async function handleAddNode(data: Partial<OrgNode>) {
    await api(`/api/account-org-map/${acctId}/nodes`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    await fetchNodes();
    setNotice({ kind: "success", text: "Person added to the org map." });
  }

  async function handleSaveNode(id: number, patch: Partial<OrgNode>) {
    await api(`/api/account-org-map/nodes/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
    await fetchNodes();
    setNotice({ kind: "success", text: "Updated." });
  }

  async function handleDeleteNode(id: number) {
    try {
      await api(`/api/account-org-map/nodes/${id}`, { method: "DELETE" });
      await fetchNodes();
      setNotice({ kind: "success", text: "Person removed." });
    } catch (e: any) {
      setNotice({ kind: "error", text: e?.message ?? "Delete failed" });
    }
  }

  async function handleApplyTemplate() {
    const industry = account?.industry ?? "";
    if (!["Automotive", "Manufacturing"].includes(industry)) return;
    if (!window.confirm(`Apply the ${industry} buying committee template to this account? This will pre-populate ${industry === "Automotive" ? "7" : "7"} roles.`)) return;
    setApplyingTemplate(true);
    try {
      const result = await api<{ inserted: number }>(`/api/account-org-map/${acctId}/apply-template`, {
        method: "POST",
        body: JSON.stringify({ industry }),
      });
      await fetchNodes();
      setNotice({ kind: "success", text: `${result.inserted} roles pre-populated from the ${industry} template.` });
    } catch (e: any) {
      setNotice({ kind: "error", text: e?.message ?? "Template apply failed" });
    } finally {
      setApplyingTemplate(false);
    }
  }

  // ─── Tree building — group children by reports_to_node_id ────────────────────

  function buildTree(nodes: OrgNode[]): { roots: OrgNode[]; childMap: Map<number, OrgNode[]> } {
    const childMap = new Map<number, OrgNode[]>();
    const roots: OrgNode[] = [];
    for (const n of nodes) {
      if (n.reports_to_node_id) {
        const siblings = childMap.get(n.reports_to_node_id) ?? [];
        siblings.push(n);
        childMap.set(n.reports_to_node_id, siblings);
      } else {
        roots.push(n);
      }
    }
    return { roots, childMap };
  }

  function renderTree(
    nodeList: OrgNode[],
    childMap: Map<number, OrgNode[]>,
    depth = 0,
  ): React.ReactNode {
    return nodeList.map((node) => {
      const children = childMap.get(node.id) ?? [];
      return (
        <div key={node.id} style={{ marginLeft: depth * 24 }} className="mt-3">
          {depth > 0 && (
            <div className="flex items-center gap-1 mb-1.5 text-[10px] text-muted-foreground uppercase tracking-wider">
              <span className="inline-block w-4 border-b border-border mr-1" />
              reports to above
            </div>
          )}
          <OrgNodeCard
            node={node}
            canEdit={canEdit()}
            onSave={handleSaveNode}
            onDelete={handleDeleteNode}
          />
          {children.length > 0 && renderTree(children, childMap, depth + 1)}
        </div>
      );
    });
  }

  // ─── Render ───────────────────────────────────────────────────────────────────

  if (!Number.isFinite(acctId) || acctId <= 0) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-[13px] text-muted-foreground">
        Invalid account id.
      </div>
    );
  }

  const { roots, childMap } = buildTree(nodes);
  const industry = account?.industry ?? "";
  const showTemplateBtn =
    canEdit() &&
    ["Automotive", "Manufacturing"].includes(industry) &&
    nodes.length === 0;

  return (
    <>
      <DocumentTitle title={account ? `Org Map — ${account.name}` : "Org Map"} />

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Back nav */}
        <div className="mb-4">
          <Link
            href={`/account/${acctId}`}
            className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground transition"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to account
          </Link>
        </div>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-muted-foreground" />
              <h1 className="text-[18px] font-semibold tracking-tight">
                Organizational Mapping
              </h1>
            </div>
            {account && (
              <p className="text-[12px] text-muted-foreground">{account.name}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            {showTemplateBtn && (
              <button
                type="button"
                onClick={handleApplyTemplate}
                disabled={applyingTemplate}
                className="text-[12px] px-3 py-1.5 rounded border border-border bg-background hover:bg-[hsl(var(--surface-2))] transition disabled:opacity-50"
                data-testid="apply-template-btn"
              >
                {applyingTemplate ? "Applying..." : `Apply ${industry} template`}
              </button>
            )}
            {canEdit() && (
              <button
                type="button"
                onClick={() => setShowAdd(true)}
                className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded border bg-foreground text-background border-foreground hover:opacity-90 transition"
                data-testid="add-person-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Add person
              </button>
            )}
          </div>
        </div>

        {/* Advisory pill */}
        <div className="mb-5">
          <span className="inline-block text-[11px] text-muted-foreground bg-[hsl(var(--surface-2))] border border-border px-3 py-1 rounded-full">
            Advisory only — does not affect Composite score. Use this to keep the buying committee organized.
          </span>
        </div>

        {/* Notice banner */}
        {notice && (
          <div
            role="status"
            className={`mb-4 px-3 py-2 rounded text-[12px] border ${
              notice.kind === "success"
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : "bg-rose-50 border-rose-200 text-rose-800"
            }`}
          >
            {notice.text}
            <button
              type="button"
              onClick={() => setNotice(null)}
              className="ml-3 underline opacity-70 hover:opacity-100"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="text-[13px] text-muted-foreground py-8 text-center">Loading...</div>
        ) : error ? (
          <div className="px-3 py-3 rounded bg-rose-50 border border-rose-200 text-rose-800 text-[12px]">
            {error}
          </div>
        ) : nodes.length === 0 ? (
          <div className="rounded border border-dashed border-border p-8 text-center text-[13px] text-muted-foreground">
            <Users className="w-8 h-8 mx-auto mb-3 opacity-30" />
            <p className="mb-1 font-medium">No people mapped yet.</p>
            <p className="text-[12px]">
              {showTemplateBtn
                ? `Use "Apply ${industry} template" to pre-populate a standard buying committee, or add people individually.`
                : "Add the people involved in this deal using the button above."}
            </p>
          </div>
        ) : (
          <div data-testid="org-tree">
            {/* Legend */}
            <div className="mb-4 flex flex-wrap gap-2 text-[10px] text-muted-foreground">
              <span className="inline-block border border-rose-600 text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full">Decision Maker</span>
              <span className="inline-block border border-rose-600 text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full">Economic Buyer</span>
              <span className="inline-block border border-emerald-600 text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">Champion</span>
              <span className="inline-block border border-orange-500 text-orange-700 bg-orange-50 px-2 py-0.5 rounded-full">Influencer</span>
              <span className="inline-block border border-gray-400 text-gray-600 bg-gray-50 px-2 py-0.5 rounded-full">User</span>
              <span className="inline-block bg-rose-600 text-white border border-rose-600 px-2 py-0.5 rounded-full">Blocker</span>
              <span className="inline-block border border-gray-300 text-gray-500 bg-gray-50 px-2 py-0.5 rounded-full">Unknown</span>
            </div>

            {/* Tree */}
            {renderTree(roots, childMap)}

            {/* Flat fallback — nodes not connected to any root */}
            {(() => {
              const allRendered = new Set<number>();
              function collect(list: OrgNode[]) {
                for (const n of list) {
                  allRendered.add(n.id);
                  const children = childMap.get(n.id) ?? [];
                  collect(children);
                }
              }
              collect(roots);
              const orphans = nodes.filter((n) => !allRendered.has(n.id));
              if (orphans.length === 0) return null;
              return (
                <div className="mt-6">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
                    Unattached
                  </div>
                  {orphans.map((node) => (
                    <div key={node.id} className="mt-3">
                      <OrgNodeCard
                        node={node}
                        canEdit={canEdit()}
                        onSave={handleSaveNode}
                        onDelete={handleDeleteNode}
                      />
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* Node count footer */}
        {nodes.length > 0 && (
          <div className="mt-6 text-[11px] text-muted-foreground text-right">
            {nodes.length} {nodes.length === 1 ? "person" : "people"} mapped
          </div>
        )}
      </div>

      {/* Add person modal */}
      {showAdd && (
        <AddNodeModal
          accountId={acctId}
          nodes={nodes}
          onSave={handleAddNode}
          onClose={() => setShowAdd(false)}
        />
      )}
    </>
  );
}
